<template>
  <div>
    <ak-box
      background="white"
      size="small"
      style="border-bottom: none !important"
      class="px py-small"
      bordered
      narrow
    >
      <ak-form
        class="one-quarter"
        style="min-width: 400px"
        @submit="fetchRejections"
      >
        <ak-input
          v-model="search"
          :loading="loading"
          placeholder="Search Contacts"
          type="search"
          icon="search"
          @right-icon-click="fetchRejections"
        />
      </ak-form>
    </ak-box>
    <ak-table
      v-model:current-page="currentPage"
      v-model:data="rejected"
      v-model:loading="loading"
      class="one-whole"
      :default-sort="defaultSort"
      :default-sort-direction="defaultSortDirection"
      :per-page="perPage"
      :total="total"
      :show-rows="shouldDisplayTableRows"
      backend-pagination
      backend-sorting
      sortable
      inset
      spacing="small"
      hoverable
      background
      paginated
      bordered
      @page="onPageChange"
      @sort="onSort"
    >
      <template #default="{ row }">
        <ak-table-column label="Contact" field="contact_fullname" sortable>
          <router-link
            :to="{ name: 'contactsEdit', params: { id: row.contact_id } }"
            class="color-link-hover"
          >
            <strong :data-transaction-id="row._id">
              {{ displayField(row.contact_fullname) }}
            </strong>
          </router-link>
          <br />
          {{ displayField(row.contact_email) }}
        </ak-table-column>
        <ak-table-column
          label="Rejected On"
          field="rejectedOn"
          width="100px"
          sortable
        >
          {{ displayDate(row.rejectedOn) }}
        </ak-table-column>
        <ak-table-column
          label="Rejected By"
          field="rejectedByName"
          width="200px"
          sortable
        >
          {{ displayField(row.rejectedByName) }}
        </ak-table-column>
        <ak-table-column label="Reason">
          <p style="max-width: 40vw" class="m-none">
            {{ displayField(row.rejected_reason) }}
          </p>
        </ak-table-column>
        <ak-table-column label=" " width="120px">
          <div class="one-whole flex flex--center">
            <ak-button color="warning" @click.prevent="undo(row)">
              Undo
            </ak-button>
          </div>
        </ak-table-column>
      </template>
      <template #empty>
        <div class="p-huge text-center">
          <ak-icon type="sad-outline" size="huge" />
          <h3>There are no Rejected Requests</h3>
        </div>
      </template>
    </ak-table>
  </div>
</template>

<script>
import { computed, ref, onMounted, defineComponent } from 'vue'
import Box from '@akerolabs/akero-vue/src/components/box/Box.vue'
import Form from '@akerolabs/akero-vue/src/components/form/Form.vue'
import Input from '@akerolabs/akero-vue/src/components/input/Input.vue'
import Table from '@akerolabs/akero-vue/src/components/table/Table.vue'
import TableColumn from '@akerolabs/akero-vue/src/components/table/TableColumn.vue'
import Button from '@akerolabs/akero-vue/src/components/button/Button.vue'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { ToastProgrammatic } from '@akerolabs/akero-vue/src/components/toast'
import { displayDate, displayField, setTabHistory } from '../utils'

export default defineComponent({
  name: 'AkRejectedTable',
  components: {
    [Box.name]: Box,
    [Form.name]: Form,
    [Input.name]: Input,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Button.name]: Button,
  },
  props: {
    /**
     * @type {String}
     * @description The current account _id
     */
    account: {
      required: true,
      type: String,
    },
    /**
     * @type {String}
     * @description The current agency _id
     */
    agency: {
      required: true,
      type: String,
    },
    /**
     * @type {Object}
     * @description The current user object
     */
    user: {
      required: true,
      type: Object,
    },
  },
  setup(props) {
    const currentPage = ref(1)
    const perPage = ref(20)
    const total = ref(0)
    const rejected = ref([])
    const loading = ref(true)
    const defaultSortDirection = ref('desc')
    const defaultSort = ref('rejectedOn')
    const search = ref('')

    /**
     * @description Should the table show a line under each row
     * @type {Boolean}
     */
    const shouldDisplayTableRows = computed(() => {
      return rejected.value.length >= 1
    })

    /**
     * @description Called by the table when a user updates the page they want to see
     * @param {Number} page
     */
    const onPageChange = async (page) => {
      currentPage.value = page
      await fetchRejections()
    }

    /**
     * @description Called by ak-table when the user updates the sort options
     * @param {String} sort
     * @param {String} direction
     */
    const onSort = async (sort, direction) => {
      defaultSortDirection.value = direction
      defaultSort.value = sort
      await fetchRejections()
    }

    /**
     * @returns {Object}
     * @description Returns the settings for the search part of the request
     * object to send to the akero API.
     */
    const searchOptions = computed(() => {
      const result = {}
      if (search.value.length > 0) {
        const query = { $regex: search.value, $options: 'i' }
        result.$or = JSON.stringify([
          { contact_fullname: query },
          { contact_email: query },
        ])
      }
      return result
    })

    /**
     * @description The current table sort options object
     * @type {Object}
     */
    const sortOptions = computed(() => {
      return {
        sort: `${defaultSortDirection.value === 'desc' ? '-' : ''}${
          defaultSort.value
        }`,
      }
    })

    /**
     * @description Returns the request object for making an api request for processed transactions
     * @type {Object}
     */
    const rejectionRequest = computed(() => {
      return {
        params: {
          ...sortOptions.value,
          ...searchOptions.value,
          account: props.account,
          agency: props.agency,
          type: 'withdraw',
          reason: 'contact_request',
          deleted: false,
          processed: { $ne: true },
          approved: { $ne: true },
          rejected: true,
          generated: { $ne: true },
          per_page: perPage.value,
          page: currentPage.value,
        },
      }
    })

    /**
     * @description Makes an api request to get the rejected transactions
     */
    const fetchRejections = async () => {
      loading.value = true
      let rejectionData = []
      let totalNum = 0
      let page = 1
      const displayError = (ex) => {
        if (ex) console.log(ex)
        ToastProgrammatic.open({
          message: `Error failed to fetch rejections`,
          type: 'danger',
        })
      }
      try {
        const { data } = await request.get(
          '/withdrawal/transactions',
          rejectionRequest.value,
        )
        rejectionData = data.data
        totalNum = data.meta.pagination.total
        page = data.meta.pagination.current
      } catch (ex) {
        rejectionData = []
        displayError(ex)
      }
      rejected.value = rejectionData
      currentPage.value = page
      total.value = totalNum
      loading.value = false
    }

    /**
     * @description Makes and api request to undo the rejection and add to its history
     * @param {Object} transaction
     */
    const undo = async (transaction) => {
      loading.value = true
      const {
        undoHistory,
        rejectedOn,
        rejectedBy,
        rejectedByName,
        rejected_reason,
      } = transaction
      const history = undoHistory ? undoHistory : []
      history.push({
        type: 'rejected',
        rejectedOn,
        rejectedBy,
        rejectedByName,
        rejected_reason,
        undoOn: Date.now(),
        undoBy: props.user._id,
        undoByName: props.user.name,
      })
      const displayError = (ex) => {
        if (ex) console.log(ex)
        ToastProgrammatic.open({
          message: `Error failed to undo rejection`,
          type: 'danger',
        })
      }
      const displaySuccess = () => {
        ToastProgrammatic.open({
          message: 'Success undo rejected',
          type: 'success',
        })
      }
      try {
        await request.post(`/withdrawal/requests/${transaction._id}`, {
          rejected: false,
          rejectedOn: null,
          rejectedBy: null,
          rejectedByName: null,
          rejected_reason: null,
          undoHistory: history,
        })
        displaySuccess()
      } catch (ex) {
        displayError(ex)
      }
      loading.value = false
      await fetchRejections()
    }

    /**
     * @description On mount fetch the rejections and set the current tab
     */
    onMounted(async () => {
      await fetchRejections()
      setTabHistory('rejected')
    })

    return {
      currentPage,
      perPage,
      total,
      rejected,
      loading,
      defaultSortDirection,
      defaultSort,
      shouldDisplayTableRows,
      onPageChange,
      onSort,
      fetchRejections,
      displayDate,
      displayField,
      undo,
      search,
    }
  },
})
</script>
